import { useEffect, useRef } from "react";

import { openTaskAt } from "../features/client/TaskModal";
import { useSafeSearchParams } from "../lib/urlState";
import { useStore } from "../store";

/**
 * Открытая публикация — в адресе: `?task=<id>&item=<id>` (02.11.2026).
 *
 * Раньше окно жило только в памяти: «Назад» уводил со страницы, ссылкой
 * поделиться было нельзя, а форма публикации не могла вернуть на ролик.
 * Теперь открытие окна — новая запись истории («Назад» закрывает его),
 * переход между роликами и публикациями внутри окна — замена (стрелки ‹ ›
 * историю не засоряют), закрытие убирает параметры. Так же, как «Модерация».
 *
 * Только на доске и в «Моих задачах»: у «Модерации» эти параметры свои.
 */
export function TaskUrlSync() {
  const [params, setParams] = useSafeSearchParams();
  const loaded = useStore((s) => s.loaded);
  const tasks = useStore((s) => s.tasks);
  const openTaskId = useStore((s) => s.openTaskId);
  const openItemId = useStore((s) => s.openItemId);
  const closeTask = useStore((s) => s.closeTask);

  const urlTask = params.get("task");
  const urlItem = params.get("item");
  // Что адрес говорил в прошлый раз — чтобы отличать «адрес сменили» от «окно сменилось».
  const seenUrl = useRef<string | null | undefined>(undefined);
  const seenOpen = useRef<string | null>(null);

  // Адрес → окно: открыли ссылку, нажали «Назад»/«Вперёд».
  useEffect(() => {
    if (!loaded) return;
    const key = urlTask ? `${urlTask}|${urlItem ?? ""}` : null;
    if (seenUrl.current === key) return;
    const state = useStore.getState();
    if (!urlTask) {
      seenUrl.current = key;
      if (state.openTaskId) closeTask();
      return;
    }
    if (state.openTaskId === urlTask && (!urlItem || state.openItemId === urlItem)) {
      seenUrl.current = key;
      return;
    }
    // Публикации нет среди загруженных (давняя, удалена) — окно не открываем;
    // догрузится при перечитывании — откроем тогда.
    if (!state.tasks.some((t) => t.id === urlTask)) return;
    seenUrl.current = key;
    openTaskAt(urlTask, urlItem);
  }, [loaded, urlTask, urlItem, tasks.length, closeTask]);

  // Окно → адрес.
  useEffect(() => {
    if (!loaded) return;
    const was = seenOpen.current;
    seenOpen.current = openTaskId;
    const want = openTaskId ? `${openTaskId}|${openItemId ?? ""}` : null;
    const have = urlTask ? `${urlTask}|${urlItem ?? ""}` : null;
    if (want === have) return;
    // Окно не открывалось и не закрывалось, а адрес уже другой — это адрес ведёт (эффект выше).
    if (!openTaskId && !was) return;
    seenUrl.current = want;
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (openTaskId) {
        next.set("task", openTaskId);
        if (openItemId) next.set("item", openItemId);
        else next.delete("item");
      } else {
        next.delete("task");
        next.delete("item");
      }
      return next;
      // Окно уже было открыто — только замена: ролик выбирается через миг после
      // открытия, когда адрес ещё не успел обновиться, и иначе выходило две записи.
    }, { replace: Boolean(have) || Boolean(was) || !openTaskId });
  }, [loaded, openTaskId, openItemId]);

  return null;
}
